import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { SettingsCard } from "@humansignal/ui";
import { useAPI } from "../../providers/ApiProvider";
import { cn } from "../../utils/bem";

/**
 * "Needs attention" — a dashboard card (same SettingsCard chrome as the
 * Progress / checklist / facilitator / rejected-work cards).
 *
 * Managers/reviewers: review queue, open disputes, open flags (QC,
 * agreement, evaluation, manual), paused annotators, activity this
 * week. Annotators: tasks left for me, my rejected items, my week.
 * Every chip is a count that links to where the work is; a chip with
 * zero stays (muted) so the card keeps its shape and "all clear" reads.
 */

const block = cn("attention");

export const AttentionRow = ({ projectId, canManage, canReview, canLabel }) => {
  const api = useAPI();
  const history = useHistory();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.callApi("projectAttention", { params: { pk: projectId }, errorFilter: () => true }).then((res) => {
      if (!cancelled && res?.project) setData(res);
    });
    return () => {
      cancelled = true;
    };
  }, [api, projectId]);

  if (!data) return null;
  const p = data.project;
  const me = data.me;
  const go = (path) => () => history.push(path);

  const items = [];
  if (canManage || canReview) {
    items.push(
      {
        key: "review",
        n: p.review_queue,
        label: "to review",
        tone: "primary",
        onClick: go(`/projects/${projectId}/review-stream`),
      },
      {
        key: "disputes",
        n: p.disputes,
        label: "disputes",
        tone: "warn",
        onClick: go(`/projects/${projectId}/review-stream`),
      },
      { key: "flags", n: p.flags, label: "flagged", tone: "warn", onClick: go(`/projects/${projectId}/review-stream`) },
    );
  }
  if (canManage) {
    items.push(
      {
        key: "paused",
        n: p.paused_members,
        label: "paused annotators",
        tone: "warn",
        onClick: go(`/projects/${projectId}/members`),
      },
      {
        key: "active",
        n: p.active_annotators_week,
        label: "active this week",
        tone: "neutral",
        onClick: go(`/projects/${projectId}/members`),
      },
      {
        key: "week",
        n: p.annotations_week,
        label: "annotations this week",
        tone: "neutral",
        onClick: go(`/projects/${projectId}/analytics`),
      },
    );
  }
  if (canLabel && !canManage) {
    items.push(
      {
        key: "remaining",
        n: me.remaining,
        label: "tasks left for you",
        tone: "primary",
        onClick: go(`/projects/${projectId}/data?labeling=1`),
      },
      {
        key: "rejected",
        n: me.rejected,
        label: "rejected to revise",
        tone: "warn",
        onClick: go(`/projects/${projectId}#rejected-work`),
      },
      { key: "myweek", n: me.this_week, label: "yours this week", tone: "neutral" },
    );
    if (me.total > 0) {
      items.push({
        key: "acc",
        n: `${Math.round((me.accepted / me.total) * 100)}%`,
        label: "accepted",
        tone: "neutral",
      });
    }
  }
  if (!items.length) return null;

  const anyWork = items.some((i) => typeof i.n === "number" && i.n > 0 && (i.tone === "primary" || i.tone === "warn"));

  return (
    <SettingsCard
      title="Needs attention"
      description={anyWork ? "Counts link to the work." : "All clear, nothing is waiting on you."}
    >
      <div className={block.elem("chips").toClassName()}>
        {items.map((i) => {
          const zero = typeof i.n === "number" && i.n === 0;
          const Tag = i.onClick && !zero ? "button" : "span";
          return (
            <Tag
              key={i.key}
              type={Tag === "button" ? "button" : undefined}
              onClick={Tag === "button" ? i.onClick : undefined}
              className={block
                .elem("chip")
                .mod({ tone: zero ? "muted" : i.tone, link: Tag === "button" })
                .toClassName()}
            >
              <span className={block.elem("num").toClassName()}>
                {typeof i.n === "number" ? i.n.toLocaleString() : i.n}
              </span>
              <span className={block.elem("label").toClassName()}>{i.label}</span>
            </Tag>
          );
        })}
      </div>
    </SettingsCard>
  );
};
